'use client';

import { useEffect, useRef, useState } from 'react';

type MiniSlide = {
  bg: string;
  render: () => React.ReactNode;
};

const SLIDES: MiniSlide[] = [
  // Fastfood
  {
    bg: '#c41e3a',
    render: () => (
      <div className="relative w-full h-full overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-[14%] bg-[#ffd400] flex items-center justify-between px-[4%]">
          <span className="text-[#c41e3a] font-black text-[clamp(8px,1.4vw,14px)]">BURGERS</span>
          <span className="text-[#c41e3a] font-black text-[clamp(6px,1vw,10px)]">PUUR · VERS · LEKKER</span>
        </div>
        <div className="absolute top-[22%] left-[4%] w-[26%] aspect-square bg-[#222] rounded overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1568901346375-23c9450c58cd?w=300&q=70"
            alt=""
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>
        <div className="absolute top-[26%] left-[33%] text-white font-black text-[clamp(11px,2vw,20px)] leading-none">
          CLASSIC<br />BURGER
        </div>
        <div className="absolute top-[48%] left-[33%] text-[#ffd400] text-[clamp(7px,1.1vw,11px)]">
          100% rundvlees, cheddar
        </div>
        <div className="absolute top-[22%] right-[4%] w-[22%] aspect-square bg-[#ffd400] rounded-full flex items-center justify-center">
          <span className="text-[#c41e3a] font-black text-[clamp(10px,1.8vw,18px)]">€8,95</span>
        </div>
        <div className="absolute bottom-[10%] left-[4%] right-[4%] h-[16%] bg-black/30 rounded flex items-center px-[3%] gap-[3%]">
          <div className="bg-[#ffd400] text-[#c41e3a] font-black text-[clamp(7px,1.2vw,11px)] px-[8%] py-[2%] rounded">
            DEAL
          </div>
          <span className="text-white text-[clamp(7px,1.2vw,11px)]">Maak er een combo van — €4,50</span>
        </div>
      </div>
    ),
  },
  // Restaurant
  {
    bg: '#fafaf7',
    render: () => (
      <div className="relative w-full h-full px-[6%] py-[8%]">
        <div className="text-[#0d7c66] font-black tracking-tight text-[clamp(13px,2.4vw,26px)] leading-none">
          VOORGERECHTEN
        </div>
        <div className="bg-[#ff6b4a] h-[2px] w-full mt-[3%]" />
        <div className="mt-[6%] space-y-[4%]">
          <div className="text-[#0f1115] text-[clamp(8px,1.4vw,14px)]">Soep van de dag — 6,50</div>
          <div className="text-[#0f1115] text-[clamp(8px,1.4vw,14px)]">Carpaccio met truffel — 12,50</div>
          <div className="text-[#0f1115] text-[clamp(8px,1.4vw,14px)]">Bruschetta met tomaat — 9,00</div>
        </div>
        <div className="absolute bottom-[8%] left-[6%] text-[#5a5d68] text-[clamp(6px,1vw,10px)]">
          Geserveerd tussen 12:00 en 16:00
        </div>
      </div>
    ),
  },
  // Wachtrij
  {
    bg: '#1a1d24',
    render: () => (
      <div className="relative w-full h-full p-[4%]">
        <div className="flex justify-between items-baseline">
          <span className="text-white/60 font-mono text-[clamp(6px,1vw,10px)] uppercase tracking-widest">
            Wachtrij
          </span>
          <span className="text-white/60 text-[clamp(6px,1vw,10px)] bg-white/10 px-[2%] py-[0.5%] rounded">
            8 wachtenden
          </span>
        </div>
        <div className="bg-[#0d7c66]/40 mt-[3%] p-[4%] rounded flex items-center gap-[4%]">
          <div className="bg-white/30 text-white w-[14%] aspect-square rounded-full flex items-center justify-center font-black text-[clamp(11px,2vw,20px)]">
            →
          </div>
          <div className="flex-1">
            <div className="text-white/80 font-mono text-[clamp(6px,1vw,10px)] uppercase tracking-widest">
              Wordt geroepen
            </div>
            <div className="text-white font-black text-[clamp(13px,2.4vw,26px)] leading-none mt-[2%]">
              A24 · Lisa
            </div>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-[3%] mt-[4%]">
          <div className="bg-white/10 p-[6%] rounded text-white">
            <div className="text-[clamp(5px,0.9vw,9px)] opacity-70 font-mono uppercase">Volgende</div>
            <div className="font-black text-[clamp(9px,1.6vw,14px)] mt-[6%]">A25 · Marco</div>
          </div>
          <div className="bg-white/5 p-[6%] rounded text-white">
            <div className="text-[clamp(5px,0.9vw,9px)] opacity-60 font-mono uppercase">Daarna</div>
            <div className="font-black text-[clamp(9px,1.6vw,14px)] mt-[6%]">A26 · Anita</div>
          </div>
          <div className="bg-white/5 p-[6%] rounded text-white">
            <div className="text-[clamp(5px,0.9vw,9px)] opacity-60 font-mono uppercase">Wachttijd</div>
            <div className="font-black text-[clamp(9px,1.6vw,14px)] mt-[6%]">±18 min</div>
          </div>
        </div>
      </div>
    ),
  },
];

const SLIDE_DURATION = 5000;

export function HeroMiniTv() {
  const [slideIdx, setSlideIdx] = useState(0);
  const [progress, setProgress] = useState(0);
  const startRef = useRef(Date.now());

  useEffect(() => {
    startRef.current = Date.now();
    setProgress(0);
    const id = setInterval(() => {
      const elapsed = Date.now() - startRef.current;
      const pct = Math.min(100, (elapsed / SLIDE_DURATION) * 100);
      setProgress(pct);
      if (elapsed >= SLIDE_DURATION) {
        setSlideIdx((i) => (i + 1) % SLIDES.length);
      }
    }, 50);
    return () => clearInterval(id);
  }, [slideIdx]);

  const slide = SLIDES[slideIdx];

  return (
    <div className="relative">
      {/* TV body */}
      <div className="bg-[#1a1d24] rounded-2xl p-2.5 md:p-3 shadow-2xl">
        <div
          className="w-full overflow-hidden rounded-md relative"
          style={{ aspectRatio: '16/9', background: slide.bg, transition: 'background 0.4s ease' }}
        >
          {slide.render()}
        </div>
        {/* Chin */}
        <div className="flex items-center justify-between gap-2 px-1 pt-2 pb-0.5">
          <div className="flex items-center gap-1.5 text-white/50 text-[8px] font-mono">
            <span className="w-1 h-1 bg-success rounded-full animate-pulse"></span>
            LIVE PREVIEW
          </div>
          <div className="flex gap-1">
            {SLIDES.map((_, i) => (
              <div
                key={i}
                className="h-[3px] rounded-full transition-all"
                style={{
                  width: i === slideIdx ? 22 : 4,
                  background:
                    i === slideIdx
                      ? `linear-gradient(to right, #ff6b4a ${progress}%, rgba(255,255,255,0.2) ${progress}%)`
                      : 'rgba(255,255,255,0.2)',
                }}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Stand */}
      <div className="mx-auto w-[18%] h-[6px] bg-[#1a1d24]/80 rounded-b" />
      <div className="mx-auto w-[40%] h-[3px] bg-[#1a1d24]/50 rounded-full mt-0.5" />

      {/* Sticker */}
      <div
        className="absolute -top-2 -right-2 bg-amber text-[#5a3a05] font-black text-[10px] px-2.5 py-1 rounded-full border-2 border-coral hidden sm:block"
        style={{ transform: 'rotate(8deg)' }}
      >
        ECHT LIVE!
      </div>
    </div>
  );
}
